/**
 * Areas of Needs — SINGLE SOURCE OF TRUTH
 * (story: proposal-vertical-taxonomy-reconcile)
 *
 * The canonical "seven sectors of human need" that the DAO's acquisition
 * proposals serve. This is the ONE place the value set, the member-facing
 * plain-language labels/descriptions, AND the axis framing copy live, so the
 * whole value set and every displayed string is a one-line change (AC1/AC5).
 *
 * Stable slug VALUES are decoupled from display LABELS on purpose: re-pinning a
 * label never changes a persisted draft's stored value. The slugs also match
 * founderyos-dashboard's on-wire `SectorTag` slugs exactly, so the two surfaces
 * speak the same taxonomy.
 *
 * ⚠ PROVISIONAL member-facing copy — pending Menley copy pin (see the story's
 * §Provisional displayed strings). Do NOT block on it. The displayed framing is
 * plain-language "areas of needs" — NEVER the internal words
 * "vertical" / "taxonomy" / "category" for THIS axis.
 */

/** The canonical seven sectors of human need (stable slug values). */
export type AreaOfNeed =
  | 'housing'
  | 'food_water'
  | 'education'
  | 'economy'
  | 'healthcare'
  | 'environment'
  | 'energy';

export interface AreaOfNeedOption {
  value: AreaOfNeed;
  /** provisional — Menley to pin */
  label: string;
  /** provisional — Menley to pin (Screen-1 warm copy) */
  description: string;
  icon: string;
}

/**
 * Canonical order per the committed "seven sectors of human need"
 * (proposal-pipeline-framework.md:55, dao-proposal-pipeline-storyboard.html:146).
 * Land exactly these 7, in this order.
 */
export const AREA_OF_NEEDS_OPTIONS: readonly AreaOfNeedOption[] = [
  { value: 'housing', label: 'Housing', description: 'Affordable homes for families', icon: '🏠' },
  { value: 'food_water', label: 'Food & Water', description: 'Farms, gardens, clean water', icon: '🌱' },
  { value: 'education', label: 'Education', description: 'Schools, training centers, workshops', icon: '📚' },
  { value: 'economy', label: 'Economy', description: 'Marketplace, co-op stores, workshops', icon: '🛒' },
  { value: 'healthcare', label: 'Healthcare', description: 'Clinics, wellness centers', icon: '⚕️' },
  { value: 'environment', label: 'Environment', description: 'Conservation, restoration, parks', icon: '🌲' },
  { value: 'energy', label: 'Energy', description: 'Solar, wind, off-grid infrastructure', icon: '⚡' },
] as const;

/** Canonical value set, in canonical order. */
export const AREA_OF_NEEDS: readonly AreaOfNeed[] = AREA_OF_NEEDS_OPTIONS.map((o) => o.value);

const OPTION_BY_VALUE: Record<AreaOfNeed, AreaOfNeedOption> = AREA_OF_NEEDS_OPTIONS.reduce(
  (acc, opt) => {
    acc[opt.value] = opt;
    return acc;
  },
  {} as Record<AreaOfNeed, AreaOfNeedOption>
);

/** Type guard: is an arbitrary string one of the canonical seven? */
export function isAreaOfNeed(value: unknown): value is AreaOfNeed {
  return typeof value === 'string' && value in OPTION_BY_VALUE;
}

/** Display label for a canonical value (falls back to the slug if unknown). */
export function areaOfNeedLabel(value: AreaOfNeed): string {
  return OPTION_BY_VALUE[value]?.label ?? value;
}

/**
 * PROVISIONAL axis-framing copy — single source (AC5). Menley pins final
 * wording here in one line. Plain-language "areas of needs" only.
 */
export const AREAS_OF_NEEDS_COPY = {
  /** step chip / step-indicator label */
  stepChip: 'Areas of needs',
  /** step heading */
  stepHeading: 'Which areas of needs?',
  /** step subhead / helper */
  stepSubhead: 'Select one or several.',
  /** review-screen / detail-page row label */
  reviewRowLabel: 'Areas of needs',
  /** desktop rollup note (hub-vs-campus wording is Menley's to pin) */
  rollupNote: (n: number) => `${n} areas selected — this proposal becomes a campus.`,
  /** short/mobile rollup note */
  rollupNoteShort: (n: number) => `${n} areas — a campus.`,
  /** rollup title suffix, e.g. "{Proposal name} — Community campus" */
  rollupTitleSuffix: 'Community campus',
} as const;

/**
 * Hub/campus rollup derivation (AC4) — EMERGENT from the selection count, NOT a
 * separately picked type and NOT an 8th pill. Selecting two or more areas makes
 * the proposal a hub/campus rollup.
 */
export function isCampusRollup(areas: readonly AreaOfNeed[] | undefined | null): boolean {
  return Array.isArray(areas) && areas.length >= 2;
}

/**
 * Normalize an arbitrary persisted value into a clean canonical selection —
 * keeps only recognized canonical slugs, de-duplicates, preserves order. Never
 * throws on unknown input.
 */
export function normalizeAreasOfNeeds(value: unknown): AreaOfNeed[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<AreaOfNeed>();
  for (const v of value) {
    if (isAreaOfNeed(v)) seen.add(v);
  }
  return [...seen];
}

/**
 * Migrate a legacy single `vertical` string
 * (`Housing | Food | Energy | Education | Community | Infrastructure | Other`)
 * to a canonical areas-of-needs array.
 *
 * Documented mapping rule (drafts must never crash or lose the schema on an
 * unknown legacy value):
 *   - Housing      → ['housing']
 *   - Food         → ['food_water']
 *   - Energy       → ['energy']
 *   - Education    → ['education']
 *   - Community    → []  (no sector-of-need equivalent — dropped; member re-picks)
 *   - Infrastructure → [] (a governance-category concept, not an area of need — dropped)
 *   - Other        → []  (dropped)
 *   - anything else / undefined → []
 * A dropped value yields an empty selection the member simply re-selects; it is
 * never a hard failure.
 */
const LEGACY_VERTICAL_MAP: Record<string, AreaOfNeed[]> = {
  Housing: ['housing'],
  Food: ['food_water'],
  Energy: ['energy'],
  Education: ['education'],
  Community: [],
  Infrastructure: [],
  Other: [],
};

export function migrateLegacyVertical(legacy: string | undefined | null): AreaOfNeed[] {
  if (typeof legacy !== 'string') return [];
  return LEGACY_VERTICAL_MAP[legacy] ?? [];
}

/** Format a selection for the on-chain text trailer + review/detail display. */
export function formatAreasOfNeeds(areas: readonly AreaOfNeed[] | undefined | null): string {
  if (!Array.isArray(areas)) return '';
  return areas.map(areaOfNeedLabel).join(' · ');
}
